import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Folder, Loader2, Pin, X } from "lucide-react";

import { AgentDropdown } from "@/components/agent-dropdown";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Agent } from "@/hooks/use-agents";
import { useAccessibleProjects } from "@/hooks/use-accessible-projects";
import { getProject, type QuickLaunchTarget } from "@/lib/quick-launch-api";
import type { AccessTokenGetter } from "@/lib/utils";
import { cn } from "@/lib/utils";

export interface QuickLaunchTargetPickerProps {
  target: QuickLaunchTarget | null;
  agents: Agent[];
  agentsLoading: boolean;
  agentsError: string | null;
  getToken: AccessTokenGetter;
  onSelect: (target: QuickLaunchTarget) => void;
  onClear: () => void;
}

/**
 * Manual target selection. Projects are fetched independently because the
 * catalog summary intentionally omits its default_agent_id; a project is
 * accepted only when that current default agent is still accessible.
 */
export function QuickLaunchTargetPicker({
  target,
  agents,
  agentsLoading,
  agentsError,
  getToken,
  onSelect,
  onClear,
}: QuickLaunchTargetPickerProps) {
  const { t } = useTranslation();
  const projectTriggerRef = useRef<HTMLButtonElement>(null);
  const projectSearchInputRef = useRef<HTMLInputElement>(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectingProjectId, setSelectingProjectId] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const {
    projects,
    hasMore,
    isLoading: projectsLoading,
    error: projectsError,
    setSearchQuery,
    loadMore,
  } = useAccessibleProjects(getToken);

  const availableAgents = useMemo(
    () => agents
      .filter((agent) => agent.type === "agent")
      .sort((left, right) => (left.order ?? 100) - (right.order ?? 100) || left.name.localeCompare(right.name)),
    [agents],
  );

  useEffect(() => {
    if (!projectOpen) {
      setQuery("");
      setSearchQuery("");
      setSelectionError(null);
    }
  }, [projectOpen, setSearchQuery]);

  const updateQuery = (value: string) => {
    setQuery(value);
    setSearchQuery(value);
  };

  const clearSearch = () => {
    updateQuery("");
    projectSearchInputRef.current?.focus();
  };

  const clearProject = () => {
    onClear();
    setSelectionError(null);
    setProjectOpen(false);
  };

  const selectAgent = (agentId: string) => {
    const agent = availableAgents.find((candidate) => candidate.id === agentId);
    if (!agent) return;
    onSelect({
      kind: "agent",
      id: agent.id,
      name: agent.name,
      description: agent.description ?? "",
    });
  };

  const selectProject = async (projectId: string) => {
    setSelectingProjectId(projectId);
    setSelectionError(null);
    try {
      const project = await getProject(projectId, getToken);
      const defaultAgent = availableAgents.find((agent) => agent.id === project.default_agent_id);
      if (!defaultAgent) {
        setSelectionError(t("quickLaunch.projectDefaultAgentUnavailable"));
        return;
      }
      onSelect({
        kind: "project",
        id: project.id,
        name: project.name,
        description: project.directive,
        default_agent_id: project.default_agent_id,
        default_agent_name: defaultAgent.name,
      });
      setProjectOpen(false);
    } catch {
      setSelectionError(t("quickLaunch.loadingTarget"));
    } finally {
      setSelectingProjectId(null);
    }
  };

  const selectedProject = target?.kind === "project" ? target : null;
  const selectedAgentId = target?.kind === "agent"
    ? target.id
    : target?.default_agent_id ?? null;
  // Keep this nested popover inside the modal Dialog's scroll shard. Radix's
  // dialog scroll lock otherwise treats a body-ported list as outside content
  // and prevents wheel/touch scrolling even though the scrollbar thumb remains
  // draggable. QuickLaunchDialog keeps its visual scroll boundary in an inner
  // child, so this portal host itself can remain overflow-visible.
  const dialogScrollShard = projectTriggerRef.current?.closest("[role=\"dialog\"]") as HTMLElement | undefined;

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-1.5"
      data-testid="quick-launch-target-picker"
    >
      <Popover open={projectOpen} onOpenChange={setProjectOpen}>
        <div className="relative min-w-0 flex-1">
          <PopoverTrigger asChild>
            <Button
              ref={projectTriggerRef}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={projectOpen}
              aria-label={t("quickLaunch.projects")}
              className="h-8 w-full justify-between gap-1 px-2 text-xs font-normal"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <Folder className={cn(
                  "size-3.5 shrink-0",
                  selectedProject ? "text-amber-500" : "text-muted-foreground",
                )} />
                <span className={cn("truncate", !selectedProject && "text-muted-foreground")}>
                  {selectedProject?.name ?? t("quickLaunch.projects")}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {/* Reserve the action slot for the sibling clear button below. */}
                {selectedProject && <span className="size-6" aria-hidden="true" />}
                <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
              </span>
            </Button>
          </PopoverTrigger>
          {selectedProject && (
            <button
              type="button"
              aria-label={t("quickLaunch.clearProject")}
              className="absolute right-6 top-1/2 z-10 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              disabled={Boolean(selectingProjectId)}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                clearProject();
              }}
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        <PopoverContent
          align="start"
          container={dialogScrollShard}
          className="z-[60] w-[min(24rem,calc(100vw-2rem))] p-0"
          onEscapeKeyDown={(event) => {
            // This is the inner popup in the dialog's Escape sequence. Close
            // it here so the next Escape is handled by the outer Dialog.
            event.preventDefault();
            setProjectOpen(false);
          }}
        >
          <Command>
            <div className="relative">
              <CommandInput
                ref={projectSearchInputRef}
                autoFocus
                value={query}
                onValueChange={updateQuery}
                placeholder={t("quickLaunch.projects")}
                className={query ? "pr-8" : undefined}
              />
              {query && (
                <button
                  type="button"
                  aria-label={t("quickLaunch.clearSearch")}
                  className="absolute right-2 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={clearSearch}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
            {selectedProject && (
              <div className="border-b px-2 py-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 text-xs"
                  disabled={Boolean(selectingProjectId)}
                  onClick={clearProject}
                >
                  <X className="size-3.5" aria-hidden="true" />
                  {t("quickLaunch.clearProject")}
                </Button>
              </div>
            )}
            <CommandList className="overscroll-contain">
              {(selectionError || projectsError || agentsError) && (
                <div className="px-3 py-2 text-sm text-destructive">
                  {selectionError ?? projectsError ?? agentsError}
                </div>
              )}
              <CommandGroup heading={t("quickLaunch.projects")}>
                {projects.map((project) => {
                  const selected = selectedProject?.id === project.id;
                  const selecting = selectingProjectId === project.id;
                  return (
                    <CommandItem
                      key={project.id}
                      value={`${project.name} ${project.description_preview}`}
                      disabled={Boolean(selectingProjectId)}
                      onSelect={() => void selectProject(project.id)}
                    >
                      {selecting ? <Loader2 className="size-4 animate-spin" /> : <Folder className="size-4 text-amber-500" />}
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-start gap-1.5">
                          {project.is_pinned && (
                            <span
                              className="mt-1 shrink-0 text-muted-foreground/60"
                              title={t("projects:catalog.pinned")}
                            >
                              <Pin className="size-3.5 stroke-[1.5] fill-none" aria-hidden="true" />
                              <span className="sr-only">{t("projects:catalog.pinned")}</span>
                            </span>
                          )}
                          <span className="min-w-0 truncate font-medium">{project.name}</span>
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{project.description_preview}</span>
                      </span>
                      {selected && <Check className="size-4 text-primary" />}
                    </CommandItem>
                  );
                })}
                {projectsLoading && projects.length === 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {t("quickLaunch.loadingProjects")}
                  </div>
                )}
                {hasMore && (
                  <div className="px-2 py-1">
                    <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => void loadMore()}>
                      {t("quickLaunch.loadingProjects")}
                    </Button>
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <AgentDropdown
        agents={availableAgents}
        currentAgentId={selectedAgentId}
        loading={agentsLoading}
        error={agentsError}
        selectionLocked={false}
        getToken={getToken}
        onSelect={selectAgent}
        className="min-w-0 flex-1"
        ariaLabel={t("quickLaunch.agents")}
        fullWidth
        isAdminUser={false}
      />
    </div>
  );
}
